/**
 * The language the kiosk is speaking.
 *
 * Japanese is the default, and on the start screen it is the leftmost button:
 * the tablet stands in a Japanese property's lobby, so the language most of
 * its guests read is the one already selected when they walk up, not one they
 * have to find first.
 *
 * It needs a home of its own because `$i18n.locale` does not survive a kiosk
 * navigation. Every screen change here is a full page load (`window.location
 * .href` rather than `$router.push` — 8. Privacy), which builds a fresh Vue
 * instance with the locale back at the app-wide default of `en`. Without
 * something carried across, "Japanese by default" would be true of the start
 * screen and false of every screen after it, and a guest who chose English
 * would be handed a Japanese form one tap later.
 *
 * Session-scoped on purpose. A trip back to the start screen ends the session
 * and drops everything the last guest did, language included — the next guest
 * finds the kiosk in Japanese, exactly as the one before them did.
 */

export const KIOSK_DEFAULT_LANGUAGE = 'ja';

const KEY = 'kiosk_language';

// Storage can throw (private browsing, storage disabled by policy). Failing
// back to the default is the safe direction: worst case the kiosk speaks
// Japanese, which is what it does when nobody has chosen anything.
export function kioskLanguage() {
    try {
        return window.sessionStorage.getItem(KEY) || KIOSK_DEFAULT_LANGUAGE;
    } catch (e) {
        return KIOSK_DEFAULT_LANGUAGE;
    }
}

export function rememberKioskLanguage(code) {
    if (!code) return;

    try {
        window.sessionStorage.setItem(KEY, code);
    } catch (e) {
        // The pick holds for this screen; the next one opens in Japanese.
    }
}

export function forgetKioskLanguage() {
    try {
        window.sessionStorage.removeItem(KEY);
    } catch (e) {
        // Nothing stored to clear.
    }
}

/**
 * Put the kiosk's language on the i18n instance. Called at the top of every
 * kiosk page's boot, which is the one moment a fresh document has a locale
 * nobody has corrected yet.
 */
export function applyKioskLanguage(i18n) {
    if (!i18n) return;

    i18n.locale = kioskLanguage();
}

/**
 * Every language written the way its own speakers write it. A guest looking
 * for their language is looking for the word they know, not its English name,
 * so the buttons never translate: 日本語 says 日本語 on the English screen too.
 */
const NATIVE_LANGUAGE_NAMES = {
    ja: '日本語',
    en: 'English',
    ko: '한국어',
    'zh-CN': '简体中文',
    'zh-TW': '繁體中文',
};

export function nativeLanguageName(code) {
    return NATIVE_LANGUAGE_NAMES[code] ?? code;
}

/**
 * The languages a facility offers, in the order the kiosk shows them.
 *
 * 日本語 leads the row wherever the facility saved it in its list: it is the
 * language the kiosk opens in, so it is the one the button row starts from.
 * The sort is stable, so everything else keeps the order staff chose.
 *
 * A facility that has saved no language list at all gets Japanese and English
 * — the pair every screen here is written in.
 */
export async function loadKioskLanguages(http, facilityId) {
    const el = await http.post('/facility-setting/list', {
        facility_id: facilityId,
        name: 'language',
        paginate: false,
    });

    const languages = el.data.total != 0
        ? el.data.data[0].value
        : [{ code: 'ja', name: 'ja' }, { code: 'en', name: 'en' }];

    return [...languages].sort(
        (a, b) => (a.code === KIOSK_DEFAULT_LANGUAGE ? 0 : 1)
            - (b.code === KIOSK_DEFAULT_LANGUAGE ? 0 : 1)
    );
}
